import { useCallback, useEffect, useRef, useState } from "react";
import { fetchApprovals, type ApprovalQueueScope } from "@/lib/api";
import type { Approval, ApprovalType, ApprovalStatus } from "@/types";

interface UseApprovalsResult {
  mine: Approval[];
  allPending: Approval[];
  completed: Approval[];
  escalated: Approval[];
  loading: boolean;
  error: string | null;
  source: "api";
  refresh: () => void;
}

// Maps the real gateway response (GatewayApprovalResponse) to the UI's
// Approval shape. The backend has no SLA/urgency/type-classification field —
// only id, transferId, status, requestedBy, policyName, required/current
// approval counts, transferSummary, risk, and dependencyState. Fields with
// no backend source are left empty rather than invented (ALL-4734 G6);
// ApprovalCard/ApprovedRow render those conditionally.
function mapApiApproval(raw: Record<string, unknown>): Approval {
  const id = String(raw.id ?? "");
  const type: ApprovalType = "transfer";
  const transferId = String(raw.transferId ?? raw.linkedResourceId ?? "");
  const policyName = String(raw.policyName ?? "");
  const requestedBy = (raw.requestedBy ?? {}) as Record<string, unknown>;
  const requester = String(requestedBy.name ?? requestedBy.id ?? "");
  const title = policyName || (transferId ? `Transfer ${transferId}` : "Approval");
  const meta = requester ? `Requested by ${requester}` : "";
  const statusRaw = String(raw.status ?? "pending").toLowerCase();
  const status: ApprovalStatus =
    statusRaw.includes("approved") ? "approved"
    : statusRaw.includes("reject") ? "rejected"
    : statusRaw.includes("escalat") ? "escalated"
    : "pending";
  const approvalsRequired = typeof raw.requiredApprovals === "number" ? raw.requiredApprovals : 1;
  const approvalsDone = typeof raw.currentApprovals === "number" ? raw.currentApprovals : 0;
  const progressPercent = approvalsRequired > 0 ? Math.round((approvalsDone / approvalsRequired) * 100) : null;

  return {
    id, type, title, meta, hint: "",
    slaLeft: "", slaSeverity: "ok",
    approvalsRequired, approvalsDone, progressPercent,
    status, urgent: false,
  };
}

async function loadScope(scope: ApprovalQueueScope): Promise<{ approvals: Approval[]; error: string | null }> {
  const result = await fetchApprovals(scope);
  if (result.reasonCode) {
    return { approvals: [], error: result.reasonCode };
  }
  return { approvals: result.approvals.map(mapApiApproval), error: null };
}

export function useApprovals(enabled = true): UseApprovalsResult {
  const [mine, setMine] = useState<Approval[]>([]);
  const [allPending, setAllPending] = useState<Approval[]>([]);
  const [completed, setCompleted] = useState<Approval[]>([]);
  const [escalated, setEscalated] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const clear = useCallback(() => {
    setMine([]);
    setAllPending([]);
    setCompleted([]);
    setEscalated([]);
    setError(null);
    setLoading(false);
  }, []);

  const load = useCallback(async () => {
    if (!enabledRef.current) {
      generation.current += 1;
      clear();
      return;
    }
    const requestGeneration = ++generation.current;
    setLoading(true);
    setError(null);
    try {
      const [mineResult, allPendingResult, completedResult, escalatedResult] = await Promise.all([
        loadScope("mine"),
        loadScope("all-pending"),
        loadScope("completed"),
        loadScope("escalated"),
      ]);
      if (requestGeneration !== generation.current) return;
      setMine(mineResult.approvals);
      setAllPending(allPendingResult.approvals);
      setCompleted(completedResult.approvals);
      setEscalated(escalatedResult.approvals);
      // Surface the first real denial/error encountered; an empty queue is
      // never an error.
      const firstError = mineResult.error ?? allPendingResult.error ?? completedResult.error ?? escalatedResult.error;
      setError(firstError);
    } catch (err) {
      if (requestGeneration !== generation.current) return;
      setMine([]);
      setAllPending([]);
      setCompleted([]);
      setEscalated([]);
      setError(err instanceof Error ? err.message : "Network error");
    } finally {
      if (requestGeneration === generation.current) setLoading(false);
    }
  }, [clear]);

  useEffect(() => {
    if (!enabled) {
      generation.current += 1;
      clear();
      return () => { generation.current += 1; };
    }
    void load();
    return () => { generation.current += 1; };
  }, [clear, enabled, load]);

  return { mine, allPending, completed, escalated, loading, error, source: "api", refresh: load };
}
